// Metadata from the approved 7 October 2026 lookup. H3 memberships stay on the API.
// Downtown Kingston combines the CSO and adjacent industrial source features.
import { KINGSTON_POSTAL_BOUNDS } from "./kingstonPostalBounds.mjs";
const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 17, 19, 20];
export const KINGSTON_POSTAL_LOOKUP_VERSION =
  "sha256:c24362237c9e7fe860f1830623e552d1bfc90f7544ff988a36ba625972d31e0d";
export const KINGSTON_POSTAL_DISTRICTS = Object.freeze(
  [
    {
      slug: "downtown-kingston",
      label: "Downtown Kingston",
      aliases: ["Downtown Kingston", "Kingston CSO", "CSO"],
    },
    ...numbers.map((number) => ({
      slug: `kingston-${number}`,
      label: `Kingston ${number}`,
      aliases: [
        `Kingston ${number}`,
        `Kingston${number}`,
        `Kgn ${number}`,
        `Kgn${number}`,
      ],
    })),
  ].map((district) =>
    Object.freeze({
      ...district,
      path: `jamaica/kingston/${district.slug}`,
      bounds: Object.freeze([...KINGSTON_POSTAL_BOUNDS[district.slug]]),
      aliases: Object.freeze(district.aliases),
    }),
  ),
);

const normal = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
export function resolveKingstonPostal(value) {
  const key = normal(value);
  return (
    KINGSTON_POSTAL_DISTRICTS.find((district) =>
      [
        district.slug,
        district.label,
        `${district.label}, Jamaica`,
        ...district.aliases,
      ].some((alias) => normal(alias) === key),
    ) || null
  );
}

// Exact numbered input is postal intent even when that number is not approved.
// This lets every search surface reject it rather than geocode a broad fallback.
export function kingstonPostalInput(value) {
  const text = String(value || "")
    .trim()
    .replace(/,?\s*jamaica$/i, "")
    .trim();
  const district = resolveKingstonPostal(text);
  return district || /^(?:kingston|kgn)[\s-]*\d+$/i.test(text)
    ? { district }
    : null;
}

export function kingstonPostalEnabled(value) {
  return value === true || value === "true";
}

// Recognise unsupported numbered districts too, so they never become radius
// searches for a similarly named community when postal search is unavailable.
export function isKingstonPostalArea(parts) {
  return (
    parts[0] === "jamaica" &&
    parts[1] === "kingston" &&
    /^(?:kingston-?\d+|kgn-?\d+|downtown-kingston|cso)$/i.test(parts[2] || "")
  );
}

export function kingstonPostalHref(
  value,
  type = "for-sale",
  { view = "", category = "" } = {},
) {
  const district = resolveKingstonPostal(
    typeof value === "object" ? value?.slug : value,
  );
  const segment = {
    sale: "for-sale",
    rent: "for-rent",
    "for-sale": "for-sale",
    "for-rent": "for-rent",
    commercial: "commercial",
  }[type];
  if (
    !district ||
    !segment ||
    !["", "map", "split"].includes(view) ||
    (category && !/^[a-z]+(?:-[a-z]+)*$/.test(category))
  )
    return null;
  return `/homes/${segment}/${view ? view + "/" : ""}${district.path}${category ? "/" + category : ""}`;
}

export function kingstonPostalLocationAliases(path) {
  return (
    KINGSTON_POSTAL_DISTRICTS.find((district) => district.path === path)
      ?.aliases || []
  );
}

export function assertKingstonPostalResponse(response, value) {
  const district = resolveKingstonPostal(
    typeof value === "object" ? value?.slug : value,
  );
  if (
    !district ||
    response?.postalDistrict !== district.slug ||
    response?.postalLookupVersion !== KINGSTON_POSTAL_LOOKUP_VERSION
  ) {
    const error = new Error(
      "Postal district results are temporarily unavailable.",
    );
    error.status = 503;
    throw error;
  }
  return response;
}
